import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Clock, ChevronDown } from 'lucide-react';
import { heroContent } from '../../data/mock';

// Industry options
const INDUSTRIES = [
  { value: "Handel", label: "Handel" },
  { value: "Dienstleistung", label: "Dienstleistung" },
  { value: "Industrie", label: "Industrie" },
  { value: "Handwerk", label: "Handwerk" },
  { value: "Chemie", label: "Chemie" },
  { value: "Gesundheit", label: "Gesundheit" },
  { value: "Logistik", label: "Logistik" },
  { value: "Bildung", label: "Bildung" },
  { value: "Sonstiges", label: "Sonstiges" }
];

// Typewriter component for animated text
const TypewriterText = () => {
  const phrases = [
    "Nachfolgeplanung",
    "Wachstum und Finanzierung", 
    "Strategische Planung"
  ];
  
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (currentText.length < currentPhrase.length) {
          setCurrentText(currentPhrase.slice(0, currentText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? 50 : 100);
    
    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentPhraseIndex, phrases]);
  
  return (
    <span className="text-[#FF0000]">
      {currentText}
      <span className="animate-blink">|</span>
    </span>
  );
};

// Parallax hook
const useParallax = () => {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return scrollY;
};

const HeroSection = () => {
  const scrollY = useParallax();
  const navigate = useNavigate();
  
  // Quick calculator state
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [revenue, setRevenue] = useState("500'000");
  const [employees, setEmployees] = useState('');
  const [foundingYear, setFoundingYear] = useState('');
  
  // Format number with Swiss format
  const formatNumber = (value) => {
    const num = value.replace(/[^0-9]/g, '');
    if (!num) return '';
    return parseInt(num).toLocaleString('de-CH').replace(/,/g, "'");
  };
  
  // Handle revenue change
  const handleRevenueChange = (e) => {
    setRevenue(formatNumber(e.target.value));
  };
  
  // Navigate to detailed evaluation with pre-filled data
  const handleStartEvaluation = () => {
    const params = new URLSearchParams();
    if (selectedIndustry) params.set('industry', selectedIndustry);
    if (revenue) params.set('revenue', revenue.replace(/'/g, ''));
    if (employees) params.set('employees', employees);
    if (foundingYear) params.set('founding_year', foundingYear);
    navigate(`/bewertung?${params.toString()}`);
  };
  
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="/hero-bg.webp" 
          alt="" 
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'grayscale(100%)' }}
          width="1500"
          height="1000"
          loading="eager"
          fetchPriority="high"
          aria-hidden="true"
        />
        
        {/* Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/50" />
      </div>

      {/* Parallax Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Parallax Circles - MORE VISIBLE */}
        <div 
          className="absolute w-72 h-72 rounded-full border-4 border-[#FF0000]/25"
          style={{ 
            top: '5%', 
            right: '3%',
            transform: `translateY(${scrollY * 0.25}px)` 
          }}
        />
        <div 
          className="absolute w-48 h-48 rounded-full bg-[#FF0000]/10"
          style={{ 
            top: '25%', 
            right: '12%',
            transform: `translateY(${scrollY * 0.18}px)` 
          }}
        />
        <div 
          className="absolute w-40 h-40 rounded-full border-2 border-[#FF0000]/30"
          style={{ 
            bottom: '15%', 
            left: '3%',
            transform: `translateY(${scrollY * -0.2}px)` 
          }}
        />
        <div 
          className="absolute w-28 h-28 rounded-full bg-[#FFE5E5]/70"
          style={{ 
            top: '55%', 
            left: '8%',
            transform: `translateY(${scrollY * -0.15}px)` 
          }}
        />
        <div 
          className="absolute w-16 h-16 rounded-full bg-[#FF0000]/15"
          style={{ 
            top: '40%', 
            right: '25%',
            transform: `translateY(${scrollY * 0.12}px)` 
          }}
        />
        
        {/* Parallax Arrows - MORE VISIBLE */}
        <img 
          src="/arrow-down-right.webp" 
          alt="" 
          className="absolute w-32 h-auto opacity-30 hidden lg:block"
          style={{ 
            top: '10%', 
            right: '6%',
            transform: `translateY(${scrollY * 0.3}px) rotate(${scrollY * 0.03}deg)` 
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-right.webp" 
          alt="" 
          className="absolute w-28 h-auto opacity-25 hidden lg:block"
          style={{ 
            bottom: '20%', 
            left: '2%',
            transform: `translateY(${scrollY * -0.25}px)` 
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-up-right.webp" 
          alt="" 
          className="absolute w-24 h-auto opacity-20 hidden xl:block"
          style={{ 
            top: '45%', 
            right: '2%',
            transform: `translateY(${scrollY * 0.18}px) rotate(${scrollY * -0.02}deg)` 
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-down.webp" 
          alt="" 
          className="absolute w-20 h-auto opacity-20 hidden lg:block"
          style={{ 
            top: '70%', 
            left: '15%',
            transform: `translateY(${scrollY * -0.12}px)` 
          }}
          aria-hidden="true"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Content */}
          <div className="space-y-6 lg:pr-12">
            {/* Trust: Schweizer Nachfolge Logo - Above Title */}
            <div className="flex items-center gap-3">
              <p className="text-xs text-[#6b6e6c]">Eine Entwicklung der</p>
              <img 
                src="/schweizer-nachfolge-logo.png" 
                alt="Schweizer Nachfolge Experten AG" 
                className="h-9 w-auto opacity-70"
              />
            </div>
            
            {/* Typewriter Header */}
            <div className="space-y-2">
              <p className="text-[#1a1c1b] text-2xl md:text-3xl font-bold" aria-live="polite">
                Unternehmensbewertung für
              </p>
              <div className="text-2xl md:text-3xl font-bold min-h-[40px]" aria-live="polite">
                <TypewriterText />
              </div>
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFE5E5] border border-[#FF0000]/30">
              <Sparkles className="w-4 h-4 text-[#FF0000]" />
              <span className="text-[#CC0000] text-sm font-medium">{heroContent.badge}</span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#1a1c1b] leading-tight max-w-lg">
              {heroContent.headline}
            </h1>

            {/* Subheadline */}
            <p className="body-large max-w-xl">
              {heroContent.subheadline}
            </p>

            {/* Description */}
            <p className="body-medium max-w-xl">
              {heroContent.description}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/bewertung" className="btn-primary gap-2 pulse-glow">
                {heroContent.ctaPrimary}
                <ArrowRight size={18} />
              </Link>
              <Link to="/kontakt" className="btn-secondary gap-2">
                {heroContent.ctaSecondary}
              </Link>
            </div>

            {/* Trust Badges - Bright Green */}
            <div className="flex flex-row flex-wrap items-center justify-start gap-x-4 gap-y-2 sm:gap-6 pt-4">
              {heroContent.trustBadges.map((badge) => (
                <div key={badge} className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#DCFCE7] flex items-center justify-center shadow-[0_0_8px_rgba(34,197,94,0.6)]">
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[#22C55E] shadow-[0_0_6px_rgba(34,197,94,0.8)]" />
                  </div>
                  <span className="text-[#5a5d5b] text-xs sm:text-sm font-medium">{badge}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Element - Interactive Calculator */}
          <div className="relative hidden lg:block lg:mt-0">
            <div 
              className="relative z-10"
              style={{ transform: `translateY(${scrollY * -0.05}px)` }}
            >
              {/* Calculator Card */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-xl">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[#1a1c1b] font-semibold text-lg">Schnellbewertung</h3>
                    <div className="px-3 py-1 rounded-full bg-[#FFE5E5] text-[#CC0000] text-xs font-medium">
                      KI-gestützt
                    </div>
                  </div>
                  
                  {/* Form */}
                  <div className="space-y-4">
                    {/* Industry Dropdown */}
                    <div>
                      <label className="text-[#5a5d5b] text-sm mb-2 block">Branche</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="w-full h-12 bg-[#f8f9fa] rounded-lg border border-gray-200 flex items-center justify-between px-4 hover:border-[#FF0000]/50 transition-colors"
                          data-testid="hero-industry-select"
                        >
                          <span className={selectedIndustry ? "text-[#1a1c1b]" : "text-[#6b6e6c]"}>
                            {selectedIndustry || "Branche auswählen..."}
                          </span>
                          <ChevronDown size={18} className={`text-[#5a5d5b] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                            {INDUSTRIES.map((industry) => (
                              <button
                                key={industry.value}
                                type="button"
                                onClick={() => {
                                  setSelectedIndustry(industry.value);
                                  setIsDropdownOpen(false);
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFE5E5] hover:text-[#CC0000] transition-colors"
                              >
                                {industry.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Revenue Input */}
                    <div>
                      <label className="text-[#5a5d5b] text-sm mb-2 block">Jahresumsatz (CHF)</label>
                      <input
                        type="text"
                        value={revenue}
                        onChange={handleRevenueChange}
                        className="w-full h-12 bg-[#f8f9fa] rounded-lg border border-gray-200 px-4 text-[#1a1c1b] focus:border-[#FF0000]/50 focus:outline-none transition-colors"
                        placeholder="0"
                        data-testid="hero-revenue-input"
                      />
                    </div>
                    
                    {/* Employees Input */}
                    <div>
                      <label className="text-[#5a5d5b] text-sm mb-2 block">Anzahl Mitarbeiter</label>
                      <input
                        type="number"
                        value={employees}
                        onChange={(e) => setEmployees(e.target.value)}
                        className="w-full h-12 bg-[#f8f9fa] rounded-lg border border-gray-200 px-4 text-[#1a1c1b] focus:border-[#FF0000]/50 focus:outline-none transition-colors"
                        placeholder="z.B. 10"
                        data-testid="hero-employees-input"
                      />
                    </div>
                    
                    {/* Founding Year Input */}
                    <div>
                      <label className="text-[#5a5d5b] text-sm mb-2 block">Gründungsjahr</label>
                      <input
                        type="number"
                        value={foundingYear}
                        onChange={(e) => setFoundingYear(e.target.value)}
                        className="w-full h-12 bg-[#f8f9fa] rounded-lg border border-gray-200 px-4 text-[#1a1c1b] focus:border-[#FF0000]/50 focus:outline-none transition-colors"
                        placeholder="z.B. 2010"
                        min="1900"
                        max="2025"
                        data-testid="hero-founding-year-input"
                      />
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="pt-4 border-t border-gray-200">
                    <button 
                      onClick={handleStartEvaluation}
                      className="w-full btn-primary block text-center"
                      data-testid="hero-start-evaluation"
                    >
                      Detaillierte Bewertung starten
                    </button>
                  </div>
                  
                  {/* Time Badge - aligned with form */}
                  <div className="flex items-center gap-3 pt-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFE5E5] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-[#FF0000]" />
                    </div>
                    <div>
                      <p className="text-[#1a1c1b] font-semibold text-sm">in 3 Min. zu Ihrer Erstanalyse</p>
                      <p className="text-[#6b6e6c] text-xs">Schnelle Ergebnisse</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badge - Kostenlos */}
              <div 
                className="absolute -top-4 -right-4 bg-[#FF0000] text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg"
                style={{ transform: `translateY(${scrollY * 0.08}px)` }}
              >
                Kostenlos
              </div>
            </div>
          </div>
          
          {/* Mobile Quick Start Card - visible only on mobile */}
          <div className="lg:hidden mt-8">
            <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[#1a1c1b] font-semibold">Schnellstart</h3>
                <div className="px-3 py-1 rounded-full bg-[#FFE5E5] text-[#CC0000] text-xs font-medium">
                  Kostenlos
                </div>
              </div>
              
              {/* Simplified mobile form */}
              <div className="space-y-3">
                <div>
                  <label className="text-[#5a5d5b] text-sm mb-1.5 block">Branche</label>
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="w-full h-12 bg-[#f8f9fa] rounded-lg border border-gray-200 px-4 text-[#1a1c1b] focus:border-[#FF0000]/50 focus:outline-none appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%235a5d5b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                    data-testid="hero-mobile-industry"
                  >
                    <option value="">Branche wählen...</option>
                    {INDUSTRIES.map((industry) => (
                      <option key={industry.value} value={industry.value}>
                        {industry.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-[#5a5d5b] text-sm mb-1.5 block">Jahresumsatz (CHF)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={revenue}
                    onChange={handleRevenueChange}
                    className="w-full h-12 bg-[#f8f9fa] rounded-lg border border-gray-200 px-4 text-[#1a1c1b] focus:border-[#FF0000]/50 focus:outline-none"
                    placeholder="z.B. 500'000"
                    data-testid="hero-mobile-revenue"
                  />
                </div>
              </div>
              
              <button 
                onClick={handleStartEvaluation}
                className="w-full btn-primary mt-4 gap-2"
                data-testid="hero-mobile-start"
              >
                Bewertung starten
                <ArrowRight size={18} />
              </button>
              
              <div className="flex items-center justify-center gap-2 mt-3 text-[#6b6e6c] text-xs">
                <Clock className="w-4 h-4" />
                <span>In 3 Minuten zu Ihrer Erstanalyse</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blink animation for cursor */}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
